import React, { useState, useEffect, useRef } from "react";
import "./CreateAssets.css";
import Headers from "Components/Header/Headers";
import Navigation from "Components/SideNavBar/Navigation";
import { useNavigate } from "react-router-dom";
import blank from "Media/blank-profile-picture.png";
import { useSelector } from "react-redux";
import { createAsset, generateAssetId } from "Config/API/action";
import {
  createClassification,
  getClassificationList,
  getGeneralInfo,
  getLocationData,
  getOrganizationConnection,
} from "Config/API/get";
import { toast } from "react-toastify";
import useFullPageLoader from "Hooks/useFullPageLoader";

const ASSET_SCOPE = "internal";
const MAX_ASSET_NUMBER_LENGTH = 25;

// Shows the dropdown on clicks inside `ref` and hides it on clicks outside
function useDropdownVisibility(ref, setShowDropdown) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowDropdown(false);
      } else {
        setShowDropdown(true);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, setShowDropdown]);
}

const CreateAssets = () => {
  const navigate = useNavigate();
  const [loader, setLoader, resetLoader] = useFullPageLoader();
  const token = localStorage.getItem("token");
  const loggedInUser = useSelector((state) => state.authenticationReducer);

  const [assetName, setAssetName] = useState("");
  const [assetNumber, setAssetNumber] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [assetLocation, setAssetLocation] = useState([]);
  const [assetLocationType, setAssetLocationType] = useState("");
  const [filteredAssetLocations, setFilteredAssetLocations] = useState([]);
  const [showLocations, setShowLocations] = useState(false);
  const [vendorName, setVendorName] = useState("");
  const [vendorNameid, setVendorNameid] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [generalInfo, setGeneralInfo] = useState("");
  const [classifications, setClassifications] = useState([]);
  const [classification, setClassification] = useState("");
  const [classificationId, setClassificationId] = useState();
  const [newClassPopup, setNewClassPopup] = useState(false);
  const [newClassification, setNewClassification] = useState("");
  const [newClassError, setNewClassError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const vendorRef = useRef(null);
  const dropdownRef = useRef(null);
  useDropdownVisibility(vendorRef, setShowDropdown);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLocations(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    getassetlocation();
  }, [token]);

  useEffect(() => {
    fetchClassification();
  }, []);

  const getloginorg = async () => {
    const info = await getGeneralInfo(token);
    if (info?.status) {
      setGeneralInfo(info?.org_info);
    }
  };

  const getassetlocation = async () => {
    try {
      const info = await getGeneralInfo(token);
      const res = await getLocationData(token, info?.org_info?.org_id);

      if (res?.status === true) {
        setAssetLocation(res.location);
      } else {
        toast.error("No asset location found");
      }
    } catch (error) {
      toast.error(`Error fetching asset location: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedAssetName = assetName.trim();
    const trimmedVendorName = vendorNameid;
    const trimmedAssetNumber = assetNumber.trim();
    const trimmedDescription = description.trim();
    const trimmedLocation = location;
    setIsSubmitting(true);

    // Every failing check is reported, not just the first one
    const validationMessages = [
      trimmedAssetName === "" && "Please enter an asset name",
      trimmedVendorName === "" && "Please select a vendor name",
      trimmedDescription === "" && "Please enter a description",
      trimmedLocation === "" && "Please enter a location",
      trimmedAssetNumber.length > MAX_ASSET_NUMBER_LENGTH &&
        "Asset number must be 25 characters or fewer.",
    ].filter(Boolean);

    if (validationMessages.length > 0) {
      setIsSubmitting(false);
      validationMessages.forEach((message) => toast(message, { type: "info" }));
      return;
    }

    setLoader();

    try {
      const generateAssetResponse = await generateAssetId(token);
      if (!generateAssetResponse?.status) {
        resetLoader();
        setIsSubmitting(false);
        toast("Failed to generate asset ID", { type: "error" });
        return;
      }

      const assetResponse = await createAsset({
        token: token,
        assetsName: trimmedAssetName,
        vendorName: trimmedVendorName,
        assetNumber: trimmedAssetNumber,
        description: trimmedDescription,
        assetId: generateAssetResponse?.data?.assetId,
        assetLocation: trimmedLocation,
        assetScope: ASSET_SCOPE,
        assetLocationType: assetLocationType,
        classification_id: classificationId,
      });
      resetLoader();
      setIsSubmitting(false);

      if (assetResponse?.status) {
        toast(assetResponse?.message, { type: "success" });
        navigate("/asset-management-view-page");
      } else {
        toast(assetResponse?.message, { type: "error" });
      }
    } catch (error) {
      resetLoader();
      setIsSubmitting(false);
      console.error("Error during asset creation:", error);
      toast("An error occurred, please try again", { type: "error" });
    }
  };

  const fetchClassification = () => {
    getClassificationList(token, loggedInUser?.org_id).then((res) => {
      if (res?.data) {
        setClassifications(res.data);
      }
    });
  };

  const addClassification = () => {
    createClassification(token, loggedInUser?.org_id, newClassification).then(
      (res) => {
        if (res?.status) {
          toast("Classification added!", { type: "success" });
          setNewClassPopup(false);
          fetchClassification();
        } else {
          toast("Failed to add classification", { type: "error" });
        }
      }
    );
  };

  const closeClassificationPopup = () => {
    setNewClassPopup(false);
    setNewClassification("");
    setNewClassError("");
  };

  const handleClassificationChange = (e) => {
    const selectedClassification = e.target.value;
    const selectedObj = classifications.find(
      (cls) => cls.classification === selectedClassification
    );

    setClassificationId(selectedObj?.id || "");
    setClassification(selectedClassification);
  };

  const handleLocationChange = (e) => {
    const locationValue = e.target.value.trimStart();
    setSelectedLocationId(locationValue);
    setShowLocations(true);
    setAssetLocationType("text");

    if (!assetLocation || assetLocation.length === 0) {
      setLocation(locationValue);
      setFilteredAssetLocations([]);
      return;
    }

    if (locationValue.length > 0) {
      const filteredLocations = assetLocation.filter((loc) =>
        loc.location.toLowerCase().includes(locationValue.toLowerCase())
      );
      setFilteredAssetLocations(filteredLocations);

      const exactMatch = filteredLocations.find(
        (loc) => loc.location.toLowerCase() === locationValue.toLowerCase()
      );

      if (exactMatch) {
        setLocation(exactMatch.id);
        setSelectedLocationId(exactMatch.location);
        setShowLocations(false);
        setAssetLocationType("id");
        return;
      }

      setLocation(locationValue);
      return;
    }

    setLocation("");
    setFilteredAssetLocations(assetLocation);
    setAssetLocationType("");
  };

  const handleVendorChange = (e) => {
    const vendorValue = e.target.value;
    setVendorName(vendorValue.trimStart());
    setVendorNameid(vendorValue.trimStart());

    if (vendorValue.length === 0) {
      // Input cleared: hide the dropdown and clear the organizations list
      setShowDropdown(false);
      setOrganizations([]);
      return;
    }

    getOrganizationConnection(token, vendorValue).then((res) => {
      if (!res?.status) {
        toast(res?.message, { type: "error" });
        setShowDropdown(false);
        setOrganizations([]);
        return;
      }

      const filteredOrganizations = res.organizationData.filter((org) =>
        org.organization_name.toLowerCase().includes(vendorValue.toLowerCase())
      );

      // Add `generalInfo` when it matches the search and is not already in the list
      const filteredGeneralInfo =
        generalInfo &&
        generalInfo.organization_name
          .toLowerCase()
          .includes(vendorValue.toLowerCase()) &&
        !filteredOrganizations.some((org) => org.id === generalInfo.id)
          ? [generalInfo]
          : [];

      setOrganizations([...filteredOrganizations, ...filteredGeneralInfo]);
      setShowDropdown(
        filteredOrganizations.length > 0 || filteredGeneralInfo.length > 0
      );
    });
  };

  const handleSelectLocation = (loc) => {
    setLocation(loc.id);
    setSelectedLocationId(loc.location);
    setShowLocations(false);
    setAssetLocationType("id");
  };

  return (
    <>
      <div className="create-assets-parent">
        <div className="create-assets-header">
          <Headers />
        </div>
        <div className="create-assets-navigation">
          <Navigation />
        </div>
        <div className="create-assets-main">
          <div className="create-asset-heading">
            <h2>Create New Asset</h2>
          </div>
          <div className="create-assets-form">
            <form
              className="create-assets-form-body"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div className="create-asset-fields">
                <label htmlFor="Asset-Name">
                  Asset Name <span>*</span>:
                </label>
                <input
                  type="text"
                  id="Asset-Name"
                  maxLength={30}
                  required
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  placeholder="Enter Name for Asset"
                />
              </div>
              <div className="create-asset-fields">
                <label htmlFor="Asset-Number">
                  Enter Asset # <span>*</span>:
                </label>
                <input
                  type="text"
                  id="Asset-Number"
                  required
                  value={assetNumber}
                  onChange={(e) =>
                    setAssetNumber(
                      e.target.value.trimStart().slice(0, MAX_ASSET_NUMBER_LENGTH)
                    )
                  }
                  placeholder="Enter Serial Number or Asset Number"
                  maxLength={30}
                />
              </div>
              <div className="create-asset-fields">
                <label htmlFor="Asset-Description">
                  Enter Description<span>*</span>:
                </label>
                <textarea
                  id="Asset-Description"
                  maxLength={200}
                  rows={4}
                  required
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter Description of Asset"
                ></textarea>
              </div>

              <div className="create-asset-fields">
                <label htmlFor="classification">
                  Classification<span>*</span>:
                </label>
                <div className="create-asset-classification classification-field-wrapper ">
                  <select
                    className="classification-select"
                    id="classification"
                    value={classification || ""}
                    required
                    onChange={handleClassificationChange}
                  >
                    <option value="">Select Classification</option>

                    {classifications.map((item) => (
                      <option key={item.id} value={item.classification}>
                        {item.classification}
                      </option>
                    ))}
                  </select>

                  <button
                    className="classification-plus-btn"
                    onClick={() => setNewClassPopup(true)}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="create-asset-fields">
                <label htmlFor="Asset-Location">
                  Asset Location <span>*</span>:
                </label>
                <div style={{ position: "relative" }} ref={dropdownRef}>
                  <input
                    type="text"
                    maxLength={30}
                    id="Asset-Location"
                    required
                    value={selectedLocationId}
                    onClick={() => {
                      getassetlocation();
                      setFilteredAssetLocations(assetLocation || []);
                      setShowLocations(true);
                    }}
                    onChange={handleLocationChange}
                    placeholder="Company Location for Asset"
                  />
                  {showLocations && filteredAssetLocations?.length > 0 && (
                    <div className="location-dropdown">
                      <ul>
                        {filteredAssetLocations.map((loc) => (
                          <li
                            key={loc.id}
                            onClick={() => handleSelectLocation(loc)}
                            className="dropdown-option"
                          >
                            {loc.location}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div className="create-asset-fields vendor-name-field">
                <label htmlFor="Asset-Vendor">
                  Vendor Name <span>*</span>:
                </label>

                <div ref={vendorRef} style={{ position: "relative" }}>
                  <input
                    type="search"
                    id="Asset-Vendor"
                    maxLength={30}
                    style={{
                      backgroundColor:
                        vendorName.length > 0 ? "#f1f2f6" : "white",
                    }}
                    value={vendorName}
                    placeholder="Search Vendor"
                    onClick={() => {
                      // Do nothing if vendorName has content
                      if (vendorName.length > 0) return;
                      getloginorg();
                    }}
                    onChange={handleVendorChange}
                  />

                  {showDropdown && organizations.length > 0 && (
                    <div className="organization-list folder-list">
                      {organizations.map((vendor, index) => (
                        <div
                          className="data-list"
                          key={vendor.id || index}
                          onClick={() => {
                            setVendorName(vendor.organization_name);
                            setVendorNameid(vendor.org_id);
                            setOrganizations([]);
                            setShowDropdown(false);
                          }}
                        >
                          <div>
                            <div className="organization-img-parent">
                              <img
                                src={vendor?.logo || blank}
                                alt="Vendor"
                                onError={(e) => {
                                  e.target.onerror = null; // Prevent infinite loop
                                  e.target.src = blank; // Fallback to demo image
                                }}
                              />
                            </div>
                            <span
                              className="multiple-line-text"
                              style={{ color: "#51595E" }}
                            >
                              {vendor.organization_name || "No Name"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="submitbutton">
                <button type="submit" disabled={isSubmitting}>
                  Submit
                </button>
              </div>
            </form>
            {newClassPopup && (
              <div className="fullscreen-popup">
                <div className="popup-box">
                  <button className="close-btn" onClick={closeClassificationPopup}>
                    ✕
                  </button>
                  <h3>Add Classification</h3>

                  <input
                    type="text"
                    placeholder="Enter classification name"
                    value={newClassification}
                    onChange={(e) => {
                      setNewClassification(e.target.value);
                      setNewClassError(""); // remove error while typing
                    }}
                    maxLength={30}
                  />

                  {newClassError && <p className="error-msg">{newClassError}</p>}

                  <div className="popup-actions">
                    <button
                      className="submit-btn-classification"
                      onClick={() => {
                        if (!newClassification.trim()) {
                          setNewClassError("Classification name is required.");
                          return;
                        }
                        addClassification();
                      }}
                    >
                      Submit
                    </button>

                    <button
                      className="cancel-btn"
                      onClick={closeClassificationPopup}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateAssets;
